import { pipelineNotificationApi } from '@/modules/pipelines/api';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useSubscription } from '@/core/messaging/export';
import { useTimer, useDebounce } from '@/shared/hooks/export';
import { NoticeHistory } from '@/modules/pipelines/model/pipeline-alerts/pipeline-alert';
import PipelineScrollerEntry from './pipeline-scroller-entry';
import { PipelineNotificationContext } from '@/modules/pipelines/contexts/pipeline-notification-context';
import PipelineTableHeader from './pipeline-table-header';
import { InfiniscrollContainer } from '@/shared/components/export';
import { useOpenWidget } from '@/core/widget-navigation/export';
import { RequirePermissions, Permissions } from '@/core/permissions/export';

function NoticeEntry({ notice }: { notice: NoticeHistory }) {
  const openWidget = useOpenWidget();

  return (
    <PipelineScrollerEntry
      notification={notice}
      onShowInfo={() => openWidget('pipeline-map', `notice/${notice._id}`)}
      onShowEvent={() => openWidget('pipeline-map', `history/${notice.groupId}/${notice._id}`)}
    />
  );
}

export default function PipelineScrollerPanel() {
  const { filters, /*setOperators,*/ events: eventTypes } = useContext(PipelineNotificationContext);
  const debouncedFilters = useDebounce(filters, 1_000);
  const [notices, setNotices] = useState<NoticeHistory[]>([]);
  const [page, setPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);

  useEffect(() => {
    setPage(0);
    setHasMore(true);

    pipelineNotificationApi.getMinimalPipelineHistory(0, debouncedFilters, eventTypes).then(v => {
      setNotices(v.content);
      setHasMore(v.content.length > 0);
    });
  }, [debouncedFilters, eventTypes]);

  useTimer(
    120_000,
    () => {
      // pipelineNotificationApi.getPipelineOperators().then(v => setOperators(v.content));

      pipelineNotificationApi.getMinimalPipelineHistory(0, debouncedFilters, eventTypes).then(v => {
        setNotices(prev => {
          const index = v.content.map(c => c?._id).indexOf(prev[0]?._id);
          if (index === 0) return prev;
          if (index !== -1) return [...v.content.slice(0, index), ...prev.slice(0, -index)];
          return [...v.content, ...prev];
        });
      });
    },
    false,
    'pa-panel-reload',
  );

  const handleMessage = (m: NoticeHistory) => {
    const {
      operator: o,
      planningStatus: pS,
      eventStatus: eS,
      eventTypeClassification: pT,
      postedBetween: pB,
      isCritical: iC,
    } = filters;
    if (iC !== undefined && m.isCritical !== iC) return;
    if (o?.length && !o.includes(m.operator._id)) return;
    if (pS?.length && !pS.includes(m.planningStatus)) return;
    if (eS?.length && !eS.includes(m.eventStatus)) return;
    if (pT?.length && !pT.includes(m.eventType)) return;
    if (pB?.start && m.effectiveStart && m.effectiveStart.valueOf() < pB.start.valueOf()) return;
    if (pB?.end && m.effectiveEnd && m.effectiveEnd.valueOf() > pB.end.valueOf()) return;

    setNotices(prev => [m, ...prev.slice(0, -1)]);
  };

  useSubscription<NoticeHistory>('/topic/pipeline-events', message => handleMessage(message));

  const loadMore = async () => {
    const newPage = page + 1;
    const response = (
      await pipelineNotificationApi.getMinimalPipelineHistory(newPage, debouncedFilters, eventTypes)
    ).content;

    if (response.length > 0) {
      setNotices(prev => [...prev, ...response]);
      setPage(newPage);
    } else {
      setHasMore(false);
    }
  };

  const displayEvents = useMemo(() => {
    const noteMap = new Map<string, NoticeHistory>();
    notices.forEach(note => {
      const existing = noteMap.get(note.groupId);
      if (!existing || note.size > existing.size) {
        noteMap.set(note.groupId, note);
      }
    });

    return Array.from(noteMap.values()).sort(
      (a, b) => new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime() || -1,
    );
  }, [notices]);

  return (
    <div className="h-100">
      <InfiniscrollContainer onLoad={loadMore} hasMore={hasMore} variant="pipeline">
        <Table hover>
          <PipelineTableHeader />

          <tbody>
            {displayEvents.map(event =>
              event.operator.devState !== 'alpha' ? (
                <NoticeEntry key={event.groupId} notice={event} />
              ) : (
                <RequirePermissions
                  key={event.groupId}
                  allow={Permissions.ALPHA_ACCESS_COMMODITIES}
                >
                  <NoticeEntry notice={event} />
                </RequirePermissions>
              ),
            )}
          </tbody>

          {!hasMore && (
            <tfoot>
              <tr>
                <td colSpan={10} className="text-center border-0" style={{ height: '3rem' }}>
                  All notices loaded
                </td>
              </tr>
            </tfoot>
          )}
        </Table>
      </InfiniscrollContainer>
    </div>
  );
}
